var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
var _a;
import { jsx as _jsx } from "react/jsx-runtime";
import { Box, Tooltip } from "@mui/material";
import CampaignOutlined from "@mui/icons-material/CampaignOutlined";
import ContactPageOutlined from "@mui/icons-material/ContactPageOutlined";
import HotelOutlined from "@mui/icons-material/HotelOutlined";
import NotesOutlined from "@mui/icons-material/NotesOutlined";
import ReceiptLongOutlined from "@mui/icons-material/ReceiptLongOutlined";
import RestaurantOutlined from "@mui/icons-material/RestaurantOutlined";
import SpaOutlined from "@mui/icons-material/SpaOutlined";
import { useTranslation } from "react-i18next";
import { NoteTypeGroup } from "../../models/app/userTypePermittedNoteTypes.model";
import { resolveNoteGroup } from "../../utils/noteTypeGroup.util";
/**
 * One icon and colour per department group, matching the groups the API's note-type catalogue
 * defines. Colours are literals rather than palette roles: the badge has to keep meaning the same
 * department across every organization's theme.
 */
export var NOTE_GROUP_ICONS = (_a = {},
    _a[NoteTypeGroup.GENERAL] = { icon: NotesOutlined, color: "#607d8b", label: "General" },
    _a[NoteTypeGroup.ACCOMMODATION] = { icon: HotelOutlined, color: "#1976d2", label: "Accommodation" },
    _a[NoteTypeGroup.SPA] = { icon: SpaOutlined, color: "#00897b", label: "Spa" },
    _a[NoteTypeGroup.RESTAURANT] = { icon: RestaurantOutlined, color: "#ef6c00", label: "Restaurant" },
    _a[NoteTypeGroup.BILLING] = { icon: ReceiptLongOutlined, color: "#6a1b9a", label: "Billing" },
    _a[NoteTypeGroup.CRM] = { icon: ContactPageOutlined, color: "#3949ab", label: "CRM" },
    _a[NoteTypeGroup.MARKETING] = { icon: CampaignOutlined, color: "#c2185b", label: "Marketing" },
    _a);
/**
 * Master switch for the department badge.
 *
 * The grouping itself stays in place — it is what the per-user note scope is built on — but the
 * badge is not shown to users. Flip this to `true` to bring every call site back at once; nothing
 * else has to change.
 */
var SHOW_NOTE_GROUP_ICON = false;
/**
 * The department badge shown to the left of a note's text.
 *
 * Currently renders nothing anywhere — see `SHOW_NOTE_GROUP_ICON`. When enabled it also renders
 * nothing for a classification the catalogue does not know: a badge naming the wrong department is
 * worse than no badge. See `resolveNoteGroup`.
 */
export var NoteGroupIcon = function (_a) {
    var note = _a.note, index = _a.index, group = _a.group, _b = _a.fontSize, fontSize = _b === void 0 ? 14 : _b, color = _a.color, _c = _a.alignToText, alignToText = _c === void 0 ? true : _c, sx = _a.sx;
    var t = useTranslation().t;
    var resolved = group !== null && group !== void 0 ? group : (index ? resolveNoteGroup(note, index) : undefined);
    if (!SHOW_NOTE_GROUP_ICON)
        return null;
    if (!resolved)
        return null;
    var mapping = NOTE_GROUP_ICONS[resolved];
    if (!mapping)
        return null;
    var Icon = mapping.icon;
    return (_jsx(Tooltip, __assign({ title: t(mapping.label), arrow: true }, { children: _jsx(Box, __assign({ component: "span", sx: __spreadArray([
                {
                    display: "inline-flex",
                    flex: "0 0 auto",
                    alignItems: "center",
                    alignSelf: alignToText ? "flex-start" : "center",
                    // Half the gap between the cap height and the line box, so the icon reads
                    // as centred on the first line however the surrounding text is sized.
                    mt: alignToText ? "0.15em" : 0,
                    cursor: "default",
                }
            ], (Array.isArray(sx) ? sx : [sx]), true) }, { children: _jsx(Icon, { "aria-label": t(mapping.label), sx: { fontSize: fontSize, color: color !== null && color !== void 0 ? color : mapping.color } }) })) })));
};
